import { AxiosResponse } from "axios";
import { ApiResponse, ApiResponseResult, ErrorResponse } from "middleware/api";
import { actions } from "reducers/slotTemplateModal";
import { actions as notificationActions } from "../reducers/notification";
import { actions as templatesActions } from "../reducers/slotTemplates";
import { actions as sessionActions } from "../reducers/session";
import * as regionsMiddleware from "middleware/regions";
import * as slotsMiddleware from "middleware/slots";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import { WorkzonesResponseType } from "./types/regions";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  TemplatesConfigResponseType,
  TemplatesCreationErrorResponseType,
  TemplatesCreationResponseType,
  SlotTemplatesDetailType,
} from "./types/slotTemplates";
import { TemplateCreationBodyType } from "reducers/types/slotTemplateModal";
import i18next from "config/i18next";
import { SlotsConstants, WeekdaysType } from "constants/slots";
import { store } from "../store";
import { TemplateFormValues } from "pages/slots/slotsTemplates/types";
import { NotificationStateType } from "reducers/types/notification";
import { connectionErrorAlert } from "utils/alerts";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getWorkzonesFetch.type, getWorkzones),
  takeLatest(actions.getDetailTemplateRequest, getTemplateDetail),
  takeLatest(actions.createTemplateRequest.type, createTemplate),
  takeLatest(actions.getTemplateConfigRequest.type, getTemplateConfig),
  takeLatest(actions.updateTemplateRequest.type, updateTemplate),
  takeLatest(actions.deleteTemplateRequest.type, deleteTemplate),
];

export default sagas;

const handleSuccess = () => {
  const { dispatch, getState } = store;
  const { filters, filtersExtra } = getState().slotTemplates;
  dispatch(
    templatesActions.getSlotTemplatesRequest({
      ...filters,
      ...filtersExtra,
    })
  );
  dispatch(templatesActions.toggleCreationModalVisibility(false));
  dispatch(templatesActions.toggleDetailModalVisibility(false));
  dispatch(sessionActions.setDirty(false));
};

const formatResponse = (response: string[]) => (
  response.map((error: string) => {
    const [code, days, startAt, finishAt] = error.split(' - ');
    return {
      code: code,
      day: days.split(',') as WeekdaysType[],
      startAt: startAt,
      finishAt: finishAt
    }
  })
)

function* getTemplateDetail({
  payload,
}: PayloadAction<number>): Generator<
  | CallEffect<AxiosResponse<ApiResponseResult<SlotTemplatesDetailType>>>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: string; type: string }>
  | PutEffect<{ payload: SlotTemplatesDetailType; type: string }>,
  void,
  AxiosResponse<ApiResponseResult<SlotTemplatesDetailType>>
> {
  const response = yield call(slotsMiddleware.getTemplateDetail, payload);
  if (response.status !== 200) {
    yield put(actions.getDetailTemplateError());
  } else {
    const { result } = response.data;
    if (result.regionId) yield put(actions.getWorkzonesFetch(result.regionId));
    yield put(actions.getDetailTemplateSuccess(result));
  }
}

function* getWorkzones({
  payload,
}: PayloadAction<string>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<WorkzonesResponseType>>>,
  void,
  ApiResponse<WorkzonesResponseType>
> {
  const response = yield call(regionsMiddleware.getWorkzonesByRegion, payload);
  if (response.status !== 200) {
    yield put(actions.getWorkzonesError());
  } else {
    const { result } = response.data;
    yield put(actions.getWorkzonesSuccess(result.items));
  }
}

function* getTemplateConfig(): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: string; type: string }>
  | CallEffect<AxiosResponse<ApiResponseResult<TemplatesConfigResponseType>>>,
  void,
  AxiosResponse<ApiResponseResult<TemplatesConfigResponseType>>
> {
  const response = yield call(slotsMiddleware.getTemplateConfig);
  if (response.status !== 200) {
    yield put(actions.getTemplateConfigError());
  } else {
    const { result } = response.data;
    yield put(actions.getTemplateConfigSuccess(result.launchDate));
  }
}

function* createTemplate({
  payload,
}: PayloadAction<TemplateFormValues>): Generator<
  | CallEffect<
      AxiosResponse<
        ApiResponseResult<TemplatesCreationResponseType> &
          TemplatesCreationErrorResponseType
      >
    >
  | PutEffect<{ type: string }>
  | void,
  void,
  AxiosResponse<
    ApiResponseResult<TemplatesCreationResponseType> &
      TemplatesCreationErrorResponseType
  >
> {
  const days = SlotsConstants.formatFrequencies(payload.frequency);
  const paramsPost: TemplateCreationBodyType = {
    regionId: payload.region?.id || "",
    workZoneId: payload.workzone?.id || "",
    startAt: `${payload.slotTime.initial}:00`,
    finishAt: `${payload.slotTime.end}:00`,
    days,
  };

  const { status, data } = yield call(
    slotsMiddleware.createTemplate,
    paramsPost
  );
  if (status !== 200 && status !== 201) {
    yield put(actions.createTemplateError());
    if (data.statusCode === SlotsConstants.TEMPLATE_OVERLAP_ERROR) {
      const overlaped = data.errors;
      const formattedResponse = formatResponse(overlaped);
      yield put(templatesActions.setOverlappedTemplates(formattedResponse));
    } else {
      yield put(notificationActions.showNotification(connectionErrorAlert()));
    }
  } else {
    yield put(actions.createTemplateSuccess());
    yield put(
      notificationActions.showNotification({
        id: "notification-slot-creation-success",
        level: "success",
        title: i18next.t("templateCreation:validTitle"),
        body: i18next.t("templateCreation:validMessage", {
          data: data.result.code,
        }),
        onClose: () => {
          handleSuccess();
        },
      })
    );
  }
}

function* updateTemplate({
  payload,
}: PayloadAction<{ id: number; body: TemplateFormValues }>): Generator<
  | CallEffect<
      AxiosResponse<
        ApiResponseResult<TemplatesCreationResponseType> &
          TemplatesCreationErrorResponseType
      >
    >
  | PutEffect<{ type: string }>
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | void,
  void,
  AxiosResponse<
    ApiResponseResult<TemplatesCreationResponseType> &
      TemplatesCreationErrorResponseType
  >
> {
  const { id, body } = payload;
  const days = SlotsConstants.formatFrequencies(body.frequency);
  const paramsPost: TemplateCreationBodyType = {
    regionId: body.region?.id || "",
    workZoneId: body.workzone?.id || "",
    startAt: `${body.slotTime.initial}:00`,
    finishAt: `${body.slotTime.end}:00`,
    days,
  };
  const { status, data } = yield call(
    slotsMiddleware.updateTemplate,
    paramsPost,
    id
  );
  if (status !== 200 && status !== 201) {
    yield put(actions.updateTemplateError());
    if (data.statusCode === SlotsConstants.TEMPLATE_OVERLAP_ERROR) {
      const overlaped = data.errors;
      const formattedResponse = formatResponse(overlaped);
      yield put(templatesActions.setOverlappedTemplates(formattedResponse));
    } else {
      yield put(notificationActions.showNotification(connectionErrorAlert()));
    }
  } else {
    yield put(actions.updateTemplateSuccess());
    yield put(
      notificationActions.showNotification({
        id: "notification-template-update-success",
        level: "success",
        title: i18next.t("templateDetail:validTitle"),
        body: i18next.t("templateDetail:validMessage"),
        onClose: () => {
          handleSuccess();
        },
      })
    );
  }
}
function* deleteTemplate({
  payload,
}: PayloadAction<number>): Generator<
  | PutEffect<{ payload: number; type: string }>
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: boolean; type: string }>
  | void
  | CallEffect<unknown>,
  void,
  ApiResponse<ErrorResponse>
> {
  const response = yield call(slotsMiddleware.deleteTemplate, payload);
  if (response.status !== 204) {
    switch (response.data?.statusCode) {
      case 404:
        yield put(
          notificationActions.showNotification({
            id: "notification-delete-error",
            level: "error",
            title: i18next.t("templateDetail:deleteErrorTitle"),
            body: i18next.t("templateDetail:deleteErrorMessage"),
            onClose: () => {
              handleSuccess();
            },
          })
        );
        break;
      default:
        yield put(notificationActions.showNotification(connectionErrorAlert()));
    }
    yield put(actions.deleteTemplateError());
  } else {
    yield put(actions.deleteTemplateSuccess());
    handleSuccess();
  }
}
